import { ref, computed } from 'vue';
import { fetchBaseServiceAPI } from '@/api/baseServiceApi';
import { DEV_MODE } from '@/config/dev-mode';
import {
  MONTHLY_FORTUNE_PARENT_TYPE_ID,
  MONTHLY_FORTUNE_CATEGORY_NUMBER_BY_TYPE_ID,
} from '@/components/saju-insight/monthly-fortune/tocCards';
import { transformMonthlyFortuneData } from '@/utils/monthlyFortuneDataTransformer';

const cache = ref({});
const inflight = new Map();

const keyOf = (guestUserId, year, month) => `${guestUserId}-${year}-${month}`;

/**
 * 구매된 typeId 목록을 base-service 의 suggestionFlag 문자열로 변환.
 *
 * - 빈 목록(미구매): "1"
 * - 부모(10) 포함(전체 구매): "3"
 * - 그 외(부분 구매): 자식 typeId 들을 카테고리 번호로 매핑해 콤마로 join (오름차순)
 */
const buildSuggestionFlag = (purchasedTypeIds) => {
  if (!purchasedTypeIds?.length) return '1';
  if (purchasedTypeIds.includes(MONTHLY_FORTUNE_PARENT_TYPE_ID)) return '3';
  const nums = purchasedTypeIds
    .map((tid) => MONTHLY_FORTUNE_CATEGORY_NUMBER_BY_TYPE_ID[tid])
    .filter((n) => n != null);
  if (!nums.length) return '1';
  return [...new Set(nums)].sort((a, b) => a - b).join(',');
};

/**
 * 낭만월운 데이터 조회.
 *
 * - analysisTypeId 는 항상 부모(10) 로 고정한다.
 * - 구매한 카테고리 번호 목록을 suggestionFlag(콤마 문자열)로 전달한다.
 * - 구매 내역이 없으면 suggestionFlag=1 로 호출 → 무료 섹션만 응답.
 */
const fetchMonthlyFortuneFreeData = async (guestUserId, year, month, purchasedTypeIds) => {
  if (!guestUserId || !year || !month) return null;
  const key = keyOf(guestUserId, year, month);
  if (cache.value[key]) return cache.value[key];
  if (inflight.has(key)) return inflight.get(key);

  const promise = (async () => {
    try {
      const res = await fetchBaseServiceAPI({
        guestUserId,
        suggestionFlag: buildSuggestionFlag(purchasedTypeIds),
        analysisTypeId: MONTHLY_FORTUNE_PARENT_TYPE_ID,
        year,
        month,
      });
      const raw = res?.data || null;
      const data = raw ? transformMonthlyFortuneData(raw) : null;
      if (data) cache.value = { ...cache.value, [key]: data };
      return data;
    } catch (e) {
      DEV_MODE && console.error('낭만월운 데이터 조회 실패:', e);
      return null;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, promise);
  return promise;
};

/**
 * @param {Ref<string|number>} guestUserIdRef
 * @param {Ref<number>} yearRef
 * @param {Ref<number>} monthRef
 * @param {(year:number, month:number) => number[]} [resolvePurchasedTypeIds]
 *   - 해당 (year, month) 에서 구매된 typeId 배열 반환 (없으면 빈 배열).
 *     부모(10) 또는 자식(42~46) 어느 쪽이든 포함될 수 있다.
 */
export const useMonthlyFortuneFreeData = (
  guestUserIdRef,
  yearRef,
  monthRef,
  resolvePurchasedTypeIds,
) => {
  const resolveTids = (y, m) => {
    if (typeof resolvePurchasedTypeIds !== 'function') return [];
    const out = resolvePurchasedTypeIds(y, m);
    return Array.isArray(out) ? out : [];
  };

  const freeData = computed(() => {
    const id = unref(guestUserIdRef);
    const y = unref(yearRef);
    const m = unref(monthRef);
    if (!id || !y || !m) return null;
    return cache.value[keyOf(id, y, m)] || null;
  });

  const ensureFreeData = () => {
    const id = unref(guestUserIdRef);
    const y = unref(yearRef);
    const m = unref(monthRef);
    return fetchMonthlyFortuneFreeData(id, y, m, resolveTids(y, m));
  };

  /** 임의의 year/month 데이터를 fetch (캐시된 경우 즉시 반환) */
  const fetchFor = (year, month) => {
    const id = unref(guestUserIdRef);
    return fetchMonthlyFortuneFreeData(id, year, month, resolveTids(year, month));
  };

  /** 캐시된 year/month 데이터 동기 조회 */
  const getFreeDataFor = (year, month) => {
    const id = unref(guestUserIdRef);
    if (!id) return null;
    return cache.value[keyOf(id, year, month)] || null;
  };

  /** 구매상태가 바뀐 경우 해당 월 캐시 무효화 후 재요청 (flag 전환 반영) */
  const invalidateMonth = (year, month) => {
    const id = unref(guestUserIdRef);
    if (!id) return;
    const k = keyOf(id, year, month);
    if (cache.value[k]) {
      const next = { ...cache.value };
      delete next[k];
      cache.value = next;
    }
    // 진행 중인 옛 요청(옛 suggestionFlag) 응답이 캐시를 다시 채우지 않도록 inflight 도 제거
    if (inflight.has(k)) inflight.delete(k);
  };

  return {
    freeData,
    ensureFreeData,
    fetchFor,
    getFreeDataFor,
    invalidateMonth,
    cache,
    fetchMonthlyFortuneFreeData,
  };
};
